import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Stack,
  Avatar,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  Computer as ComputerIcon,
  CalendarToday as CalendarIcon,
  Language as LanguageIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
// Removed date picker imports to fix compatibility issues
import toast from 'react-hot-toast';
import { emailsAPI } from '../../services/api';

const FormEmailsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    websiteId: '',
    status: '',
    startDate: null,
    endDate: null,
    search: ''
  });
  
  // UI States
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedSubmissionForMenu, setSelectedSubmissionForMenu] = useState(null);
  
  // Bulk operations
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    fetchAnalytics();
  }, [page, rowsPerPage, filters]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
        startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
      };

      const response = await emailsAPI.getEmails(params);
      setSubmissions(response.data.submissions);
      setWebsites(response.data.websites);
      setTotalSubmissions(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load form submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await emailsAPI.getAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setDetailDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = (submission) => {
    setSubmissionToDelete(submission);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await emailsAPI.deleteEmail(submissionToDelete._id);
      toast.success('Form submission deleted successfully');
      setDeleteDialogOpen(false);
      setSubmissionToDelete(null);
      fetchSubmissions();
      fetchAnalytics();
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const handleStatusUpdate = async (submissionId, newStatus, notes = '') => {
    try {
      await emailsAPI.updateStatus(submissionId, newStatus, notes);
      toast.success('Status updated successfully');
      fetchSubmissions();
      fetchAnalytics();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleMenuClick = (event, submission) => {
    setMenuAnchor(event.currentTarget);
    setSelectedSubmissionForMenu(submission);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedSubmissionForMenu(null);
  };

  const handleSelectSubmission = (submissionId) => {
    setSelectedSubmissions(prev => {
      if (prev.includes(submissionId)) {
        return prev.filter(id => id !== submissionId);
      } else {
        return [...prev, submissionId];
      }
    });
  };

  const handleSelectAllSubmissions = (event) => {
    if (event.target.checked) {
      setSelectedSubmissions(submissions.map(s => s._id));
    } else {
      setSelectedSubmissions([]);
    }
  };

  const handleBulkDeleteClick = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const response = await emailsAPI.bulkDeleteEmails(selectedSubmissions);
      toast.success(`${response.data.submissionsDeleted} submissions and ${response.data.filesDeleted} files deleted successfully`);
      setBulkDeleteDialogOpen(false);
      setSelectedSubmissions([]);
      fetchSubmissions();
      fetchAnalytics();
    } catch (error) {
      console.error('Error bulk deleting submissions:', error);
      toast.error('Failed to delete submissions');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processed':
        return <CheckCircleIcon color="success" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'spam':
        return <BlockIcon color="error" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'spam':
        return 'error';
      default:
        return 'default';
    }
  };

  const clearFilters = () => {
    setFilters({
      websiteId: '',
      status: '',
      startDate: null,
      endDate: null,
      search: ''
    });
    setPage(0);
  };



  if (loading && submissions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading form submissions...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            Form Submissions
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchSubmissions();
              fetchAnalytics();
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Analytics Cards */}
        {!analyticsLoading && analytics && (
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      <EmailIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{analytics.totalSubmissions}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Submissions
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                      <CalendarIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{analytics.todaySubmissions}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Today
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                      <TrendingUpIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{analytics.thisWeekSubmissions}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        This Week
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                      <PendingIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{analytics.statusBreakdown.pending}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Filters"
            action={
              <Button size="small" onClick={clearFilters}>
                Clear All
              </Button>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Website</InputLabel>
                  <Select
                    value={filters.websiteId}
                    onChange={(e) => handleFilterChange('websiteId', e.target.value)}
                    label="Website"
                  >
                    <MenuItem value="">All Websites</MenuItem>
                    {websites.map((website) => (
                      <MenuItem key={website._id} value={website._id}>
                        {website.name} ({website.siteId})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="processed">Processed</MenuItem>
                    <MenuItem value="spam">Spam</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={filters.startDate ? filters.startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFilterChange('startDate', e.target.value ? new Date(e.target.value) : null)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  value={filters.endDate ? filters.endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFilterChange('endDate', e.target.value ? new Date(e.target.value) : null)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search by IP address or notes..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Submissions Table */}
        <Card>
          <CardHeader
            title="Form Submissions"
            action={
              selectedSubmissions.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleBulkDeleteClick}
                  size="small"
                >
                  Delete Selected ({selectedSubmissions.length})
                </Button>
              )
            }
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedSubmissions.length > 0 && selectedSubmissions.length < submissions.length}
                      checked={submissions.length > 0 && selectedSubmissions.length === submissions.length}
                      onChange={handleSelectAllSubmissions}
                    />
                  </TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>Name & Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="center">Files</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : submissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No form submissions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  submissions.map((submission) => {
                    const formData = submission.formattedData || submission.formData;
                    return (
                      <TableRow key={submission._id} hover selected={selectedSubmissions.includes(submission._id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedSubmissions.includes(submission._id)}
                            onChange={() => handleSelectSubmission(submission._id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {submission.website.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {submission.website.siteId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {formData.name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formData.email || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(submission.status)}
                            label={submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                            color={getStatusColor(submission.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(submission.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(submission.createdAt).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {submission.files && submission.files.length > 0 ? (
                            <Tooltip title={`${submission.files.length} file(s) attached`}>
                              <Chip
                                icon={<AttachFileIcon />}
                                label={submission.files.length}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No files
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EmailIcon />}
                            onClick={() => handleViewSubmission(submission)}
                            sx={{ minWidth: 'auto' }}
                          >
                            Open Email
                          </Button>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, submission)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={totalSubmissions}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Card>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleViewSubmission(selectedSubmissionForMenu)}>
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleStatusUpdate(selectedSubmissionForMenu?._id, 'processed')}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Mark as Processed</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleStatusUpdate(selectedSubmissionForMenu?._id, 'spam')}>
            <ListItemIcon>
              <BlockIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Mark as Spam</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleDeleteClick(selectedSubmissionForMenu)} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Form Submission Details
          </DialogTitle>
          <DialogContent>
            {selectedSubmission && <SubmissionDetails submission={selectedSubmission} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Form Submission</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone. All associated files will also be deleted from storage.
            </Alert>
            <Typography>
              Are you sure you want to delete this form submission?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog
          open={bulkDeleteDialogOpen}
          onClose={() => setBulkDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Multiple Form Submissions</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This action cannot be undone. All associated files will also be deleted from storage.
            </Alert>
            <Typography>
              Are you sure you want to delete {selectedSubmissions.length} form submission{selectedSubmissions.length > 1 ? 's' : ''}?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will permanently remove all selected submissions and their attached files from Backblaze B2 storage.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkDeleteConfirm}
              color="error"
              variant="contained"
            >
              Delete {selectedSubmissions.length} Submission{selectedSubmissions.length > 1 ? 's' : ''}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
};

// Submission Details Component
const SubmissionDetails = ({ submission }) => {
  const formData = submission.formattedData || submission.formData;
  
  return (
    <Box>
      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader title="Basic Information" />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Website
                  </Typography>
                  <Typography variant="body1">
                    {submission.website.name} ({submission.website.siteId})
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                    color={submission.status === 'processed' ? 'success' : submission.status === 'pending' ? 'warning' : 'error'}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Submitted
                  </Typography>
                  <Typography variant="body1">
                    {new Date(submission.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Technical Info */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader title="Technical Information" />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    IP Address
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {submission.submitterInfo.ip}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    User Agent
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {submission.submitterInfo.userAgent}
                  </Typography>
                </Box>
                {submission.submitterInfo.referer && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Referer
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {submission.submitterInfo.referer}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Form Data */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title="Form Data" />
            <CardContent>
              <Grid container spacing={2}>
                {Object.entries(formData).map(([key, value]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </Typography>
                      <Typography variant="body1">
                        {value || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* File Attachments */}
        {submission.files && submission.files.length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader 
                title="File Attachments" 
                avatar={<AttachFileIcon />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  {submission.files.map((file, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 2, 
                          display: 'flex', 
                          alignItems: 'center',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        <Box sx={{ mr: 2 }}>
                          {file.category === 'image' && <ImageIcon color="primary" />}
                          {file.category === 'pdf' && <PdfIcon color="error" />}
                          {(file.category === 'document' || file.category === 'spreadsheet') && <DocumentIcon color="info" />}
                          {(file.category === 'text' || file.category === 'other') && <AttachFileIcon />}
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {file.originalName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {file.humanReadableSize || `${Math.round(file.size / 1024)} KB`}
                          </Typography>
                          <br />
                          <Chip 
                            label={file.category} 
                            size="small" 
                            variant="outlined"
                            color={
                              file.category === 'image' ? 'primary' :
                              file.category === 'pdf' ? 'error' :
                              file.category === 'document' ? 'info' : 'default'
                            }
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => window.open(file.downloadUrl, '_blank')}
                          title="Download file"
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Notes */}
        {submission.notes && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader title="Notes" />
              <CardContent>
                <Typography variant="body1">
                  {submission.notes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default FormEmailsPage; 